/**
 * "A pessoa está aí?" — um sinal, um jogo de listeners, dois consumidores.
 *
 * Nasceu **extraído** de `useIdleDetector`, que já rastreava exatamente isto para
 * emitir `presence_update {active|afk}` e não expunha nada: o estado morria
 * dentro de um `useEffect` guardado por `socket && socketReady`. Quando a decisão
 * "toast ou notificação do SO" passou a precisar do mesmo fato
 * ([[../notifications/foco]]), havia dois caminhos — duplicar os listeners ou
 * extrair a fonte. Duplicar sairia caro de verdade: `mousemove` dispara dezenas
 * de vezes por segundo, e dois handlers em vez de um é custo por movimento do
 * mouse, não custo de montagem. Além disso duas cópias divergem: bastaria um
 * evento novo entrar numa lista e não na outra para "ativo" significar coisas
 * diferentes em duas telas do mesmo app.
 *
 * ## Por que o rastreio se arma sozinho
 *
 * Os consumidores são de duas naturezas: quem **observa** (o detector de idle,
 * que precisa saber a hora em que a atividade acontece) e quem **pergunta** (o
 * caminho de notificação, que só quer saber se houve atividade recentemente).
 * Se o rastreio dependesse de alguém observar, a resposta a quem pergunta seria
 * silenciosamente falsa sempre que o `useIdleDetector` não estivesse montado —
 * e ele não está montado enquanto o socket não conecta.
 *
 * Por isso qualquer entrada (observar OU perguntar) arma. A primeira leitura
 * marca o instante como atividade: um app recém-carregado não é um app
 * abandonado.
 *
 * ## Por que os listeners não saem
 *
 * Não há `desligar()`. Os cinco listeners são `passive` no `window` e vivem o
 * tempo do documento — mesma ordem de grandeza de um listener de layout. Sair e
 * voltar ao ar conforme o número de observadores só criaria a janela em que o
 * relógio para de andar sem ninguém perceber, que é a falha silenciosa que este
 * módulo existe para não ter. Baixa de assinante existe (`observeUserActivity`
 * devolve a função); baixa do rastreio, não.
 */

/**
 * Os mesmos cinco eventos do `useIdleDetector` desde sempre. Cobrem ponteiro,
 * teclado e toque — `scroll` está aqui porque ler uma conversa longa é atividade
 * legítima sem clique nenhum.
 */
export const USER_ACTIVITY_EVENTS = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
] as const

/** 5 min — o mesmo prazo com que a presença marca `afk`. Um número só. */
export const USER_IDLE_MS = 5 * 60 * 1000

let ultimaAtividade = 0
let armado = false
const assinantes = new Set<() => void>()

function marcar(): void {
  ultimaAtividade = Date.now()
  // Cópia: um assinante pode dar baixa de dentro do próprio callback.
  for (const avisar of [...assinantes]) avisar()
}

function aoMudarVisibilidade(): void {
  // Só a VOLTA conta. Aba indo pro segundo plano não é atividade — é o oposto.
  if (document.visibilityState === 'visible') marcar()
}

function armar(): void {
  if (armado || typeof window === 'undefined') return
  armado = true
  ultimaAtividade = Date.now()
  for (const evento of USER_ACTIVITY_EVENTS) {
    window.addEventListener(evento, marcar, { passive: true })
  }
  document.addEventListener('visibilitychange', aoMudarVisibilidade)
}

/**
 * Assina as notificações de atividade. Devolve a função de baixa.
 *
 * Quem só quer saber o estado não precisa disto — use `isUserActive`.
 */
export function observeUserActivity(aoAtivar: () => void): () => void {
  armar()
  assinantes.add(aoAtivar)
  return () => {
    assinantes.delete(aoAtivar)
  }
}

/** Quanto tempo faz desde a última atividade. Arma o rastreio se preciso. */
export function msSinceUserActivity(): number {
  armar()
  return Date.now() - ultimaAtividade
}

/** Houve atividade dentro do prazo. */
export function isUserActive(idleMs: number = USER_IDLE_MS): boolean {
  return msSinceUserActivity() < idleMs
}

/**
 * Só para teste. O estado é de módulo e sobrevive entre casos; sem isto um teste
 * que avança o relógio contaminaria o seguinte.
 */
export function resetUserActivityTracking(): void {
  if (armado && typeof window !== 'undefined') {
    for (const evento of USER_ACTIVITY_EVENTS) {
      window.removeEventListener(evento, marcar)
    }
    document.removeEventListener('visibilitychange', aoMudarVisibilidade)
  }
  armado = false
  ultimaAtividade = 0
  assinantes.clear()
}
